import React, { useEffect, useRef, useState } from 'react';
import './ClearancePopup.css';
import { apiFetch } from '../services/api';
import { useSettingsState } from '../context/SettingsContext';
import { isPrerendering } from '../prerenderData';
import ClearancePopupCard, { resolvePopupContent } from './ClearancePopupCard';

// LocalStorage flag — set on dismiss/submit so the popup doesn't reappear.
const STORAGE_KEY = 'asg.clearancePopup.dismissed';
// Default first-show delay in ms (admin-overridable via popup_delay_seconds).
const SHOW_DELAY_MS = 3000;
// Append `?popup=force` to any URL to force the popup open (bypasses the
// localStorage dismissed flag). Useful for QA and screenshots.
const FORCE_PARAM = 'popup';
const FORCE_VALUE = 'force';

function ClearancePopup() {
  const { settings, loading } = useSettingsState();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Behaviour (admin → Storefront → Content → Clearance Popup).
  const enabled = settings.popup_enabled !== false;
  const delaySec = Number(settings.popup_delay_seconds);
  const delayMs = Number.isFinite(delaySec) && delaySec >= 0 ? delaySec * 1000 : SHOW_DELAY_MS;
  // Editable content, with defaults applied (shared with the admin preview).
  const content = resolvePopupContent(settings);

  // Mount-time: show after the configured delay unless disabled or already
  // dismissed. Waits for settings to load so the configured delay is honoured.
  //
  // Never while pre-rendering. The pre-renderer waits long enough for the
  // delay to elapse, so the open dialog was being captured into every
  // page's HTML - a "20% OFF" heading in every crawled page, and a node the
  // client's first render does not produce, which failed hydration (#418)
  // on every route and made React rebuild each page from scratch.
  useEffect(() => {
    if (typeof window === 'undefined' || isPrerendering) return undefined;
    const forced = new URLSearchParams(window.location.search).get(FORCE_PARAM) === FORCE_VALUE;
    if (forced) {
      setOpen(true);
      return undefined;
    }
    if (loading || !enabled) return undefined;
    let dismissed = false;
    try { dismissed = window.localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { /* storage blocked */ }
    if (dismissed) return undefined;
    const t = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(t);
  }, [loading, enabled, delayMs]);

  // Focus the email input when the popup opens.
  useEffect(() => {
    if (open && inputRef.current) {
      // Defer one frame so the transition can start cleanly.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Esc key dismisses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) { setError('Please enter your email.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/contacts/clearance-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
      // Auto-dismiss after a short thank-you moment.
      setTimeout(() => dismiss(), 2400);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="clp-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clp-title"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      {submitted ? (
        <div className="clp-card">
          <div className="clp-thanks">
            <div className="clp-thanks-tick" aria-hidden="true">✓</div>
            <h2 className="clp-thanks-title">You're in.</h2>
            <p className="clp-thanks-sub">Check your inbox - your code is on the way.</p>
          </div>
        </div>
      ) : (
        <ClearancePopupCard
          {...content}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onSubmit={handleSubmit}
          submitting={submitting}
          error={error}
          onDecline={dismiss}
          inputRef={inputRef}
        />
      )}
    </div>
  );
}

export default ClearancePopup;
